// components/site-header.tsx
"use client";

import { ChevronDown, LogOut, Menu, User, X } from "lucide-react";
import Link from "next/link";
import { usePathname, useRouter } from "next/navigation";
import { useEffect, useRef, useState } from "react";

import { authService } from "@/app/auth/services_and_hooks/authService";
import { useAuthHook } from "@/app/auth/services_and_hooks/useAuthHook";
import { useMyStaffAccessHook } from "@/app/features/staff_access/services_and_hooks/useStaffAccessHooks";
import { HTFButton } from "@/app/shared/components/button/HTFButtonComponent";
import { isBreakGlassSuperAdmin } from "@/lib/user-management/types";

const PLAN_NAMES: Record<string, string> = {
  price_1RXXpC2VJI5a59aGN4Rowby7: "Free",
  price_1RXX302VJI5a59aGY6v5kzDz: "Starter",
  price_1RXXrt2VJI5a59aG2L39Pi83: "Pro",
};

interface SiteHeaderProps {
  hideGetStarted?: boolean;
}

export function SiteHeader({ hideGetStarted = false }: SiteHeaderProps) {
  const pathname = usePathname();
  const router = useRouter();

  const {
    user,
    isLoading,
    isAuthenticated,
    subscription,
    hasActiveSubscription,
  } = useAuthHook();

  const { data: staffAccess } = useMyStaffAccessHook(!!user?.email);
  const showAdminLink =
    isBreakGlassSuperAdmin(user?.email) || staffAccess != null;

  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isHeaderVisible, setIsHeaderVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);

  const userMenuRef = useRef<HTMLDivElement>(null);
  const mobileMenuRef = useRef<HTMLDivElement>(null);

  // 🔥 SCROLL DETECTION LOGIC
  useEffect(() => {
    let lastScrollYRef = lastScrollY;

    const handleScroll = () => {
      const currentScrollY = window.scrollY;

      // Only hide header after scrolling down 100px to avoid jitter
      if (currentScrollY < 100) {
        setIsHeaderVisible(true);
      } else if (currentScrollY > lastScrollYRef && currentScrollY > 100) {
        // Scrolling down - hide header
        setIsHeaderVisible(false);
        setIsUserMenuOpen(false);
        setIsMobileMenuOpen(false);
      } else if (currentScrollY < lastScrollYRef) {
        // Scrolling up - show header
        setIsHeaderVisible(true);
      }

      lastScrollYRef = currentScrollY;
      setLastScrollY(currentScrollY);
    };

    // Add scroll listener with throttling
    let ticking = false;
    const scrollListener = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          handleScroll();
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener("scroll", scrollListener, { passive: true });
    return () => window.removeEventListener("scroll", scrollListener);
  }, []); // Remove lastScrollY dependency to prevent infinite loop

  const handleSignOut = async () => {
    setIsUserMenuOpen(false);
    setIsMobileMenuOpen(false);
    await authService.signOut();
  };

  const toggleUserMenu = () => setIsUserMenuOpen(!isUserMenuOpen);
  const toggleMobileMenu = () => setIsMobileMenuOpen(!isMobileMenuOpen);

  // Close menus when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        userMenuRef.current &&
        !userMenuRef.current.contains(event.target as Node)
      ) {
        setIsUserMenuOpen(false);
      }
      if (
        mobileMenuRef.current &&
        !mobileMenuRef.current.contains(event.target as Node)
      ) {
        setIsMobileMenuOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setIsMobileMenuOpen(false);
    setIsUserMenuOpen(false);
  }, [pathname]);

  const signedOutNavigation = [
    // { name: "Home", href: "/" },
    { name: "AI for Finance", href: "/solutions/industries/finance" },
    { name: "AI Account Manager", href: "/solutions/ai-account-manager" },
    { name: "Customers", href: "/customers" },
    { name: "Contact", href: "/contact" },
    // { name: "Services", href: "/services" },
    // { name: "Solutions", href: "/solutions" },
    // { name: "Products", href: "/products" },
    // { name: "Learn", href: "/learn" },
    // { name: "About", href: "/about" },
  ];

  const signedInNavigation = [
    { name: "Dashboard", href: "/dashboard" },
    { name: "AI Workflows", href: "/ai-workflows" },
    { name: "AI Examples", href: "/ai-examples" },
    { name: "AI News", href: "/news" },
    { name: "AI Templates", href: "/templates" },
    // { name: "AI Models", href: "/models" },
  ];

  const currentNavigation = isAuthenticated
    ? signedInNavigation
    : signedOutNavigation;

  const renderUserActions = () => {
    if (isLoading) {
      return (
        <div className="flex items-center gap-2">
          <div className="w-20 h-9 bg-gray-200 rounded-lg animate-pulse" />
          <div className="w-24 h-9 bg-gray-300 rounded-lg animate-pulse" />
        </div>
      );
    }

    if (isAuthenticated) {
      return (
        <>
          <div className="hidden lg:flex items-center gap-2">
            {!hasActiveSubscription && (
              <Link href="/pricing">
                {/* 🔥 FIXED: Better upgrade button color using teal */}
                <HTFButton
                  variant="primary"
                  size="sm"
                  // className="bg-teal hover:bg-teal-dark text-white border-teal hover:border-teal-dark shadow-md"
                >
                  Upgrade
                </HTFButton>
              </Link>
            )}
          </div>

          <div className="relative" ref={userMenuRef}>
            <button
              onClick={toggleUserMenu}
              className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-gray-50 transition-all duration-200"
            >
              <div className="w-7 h-7 rounded-full bg-teal/10 text-teal-dark flex items-center justify-center">
                <User size={16} />
              </div>
              <span className="text-sm font-display font-semibold hidden sm:inline">
                {user?.email?.split("@")[0] || "Account"}
              </span>
              <ChevronDown size={16} className="text-gray-500" />
            </button>

            {isUserMenuOpen && (
              <div className="absolute right-0 mt-2 w-72 bg-white rounded-xl shadow-lg py-2 z-50 border border-gray-100">
                <div className="px-4 py-3 border-b border-gray-100">
                  <p className="text-xs text-gray-500 uppercase tracking-wider">
                    Signed in as
                  </p>
                  <p className="text-sm font-semibold text-gray-900 truncate">
                    {user?.email}
                  </p>
                  <div className="flex items-center gap-2 mt-1">
                    <div className="flex items-center gap-1">
                      <div
                        className={`w-2 h-2 rounded-full ${hasActiveSubscription ? "bg-green-500" : "bg-gray-400"}`}
                      />
                      <p className="text-xs text-gray-500">
                        {hasActiveSubscription
                          ? PLAN_NAMES[subscription?.priceId || ""] || "Pro"
                          : "Free Plan"}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="py-1">
                  <button
                    onClick={() => {
                      router.push("/dashboard");
                      setIsUserMenuOpen(false);
                    }}
                    className="w-full flex items-center gap-3 px-4 py-2.5 text-sm font-display font-medium text-gray-700 hover:bg-gray-50 text-left transition-colors"
                  >
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z"
                      />
                    </svg>
                    Dashboard
                  </button>

                  <button
                    onClick={() => {
                      router.push("/ai-workflows");
                      setIsUserMenuOpen(false);
                    }}
                    className="w-full flex items-center gap-3 px-4 py-2.5 text-sm font-display font-medium text-gray-700 hover:bg-gray-50 text-left transition-colors"
                  >
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
                      />
                    </svg>
                    AI Workflows
                  </button>

                  {!hasActiveSubscription && (
                    <Link
                      href="/pricing"
                      onClick={() => setIsUserMenuOpen(false)}
                      className="w-full flex items-center gap-3 px-4 py-2.5 text-sm font-display font-medium text-gray-700 hover:bg-gray-50 text-left transition-colors"
                    >
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z"
                        />
                      </svg>
                      Upgrade Plan
                    </Link>
                  )}

                  <button
                    onClick={() => {
                      router.push("/organizations");
                      setIsUserMenuOpen(false);
                    }}
                    className="w-full flex items-center gap-3 px-4 py-2.5 text-sm font-display font-medium text-gray-700 hover:bg-gray-50 text-left transition-colors"
                  >
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 8h5"
                      />
                    </svg>
                    Organizations
                  </button>

                  <button
                    onClick={() => {
                      router.push("/settings");
                      setIsUserMenuOpen(false);
                    }}
                    className="w-full flex items-center gap-3 px-4 py-2.5 text-sm font-display font-medium text-gray-700 hover:bg-gray-50 text-left transition-colors"
                  >
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
                      />
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                      />
                    </svg>
                    Settings
                  </button>

                  {showAdminLink && (
                    <>
                      <div className="border-t border-gray-100 my-1" />
                      <button
                        onClick={() => {
                          router.push("/admin");
                          setIsUserMenuOpen(false);
                        }}
                        className="w-full flex items-center gap-3 px-4 py-2.5 text-sm font-display font-medium text-amber-700 hover:bg-amber-50 text-left transition-colors"
                      >
                        <svg
                          className="w-4 h-4"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4"
                          />
                        </svg>
                        Admin Dashboard
                      </button>
                    </>
                  )}

                  <div className="border-t border-gray-100 my-1" />
                  <button
                    onClick={handleSignOut}
                    className="w-full flex items-center gap-3 px-4 py-2.5 text-sm font-display font-medium text-red-600 hover:bg-red-50 text-left transition-colors"
                  >
                    <LogOut size={16} />
                    Logout
                  </button>
                </div>
              </div>
            )}
          </div>
        </>
      );
    }

    return (
      <div className="hidden sm:flex gap-3 items-center">
        <Link
          href="/login"
          className="text-sm font-display font-semibold text-gray-700 hover:text-teal-dark transition-colors px-3 py-2 rounded-lg hover:bg-teal/5"
        >
          Login
        </Link>
        <HTFButton
          asChild
          variant="primary"
          size="default"
          className="shadow-lg text-black"
        >
          <Link href="/contact?source=header">Book Discovery Call</Link>
        </HTFButton>
      </div>
    );
  };

  return (
    <>
      {/* 🔥 FIXED: Clean header with no borders or white lines */}
      <header
        className={`fixed top-0 z-50 w-full bg-beige-dark backdrop-blur-sm transition-transform duration-300 ease-in-out ${
          isHeaderVisible ? "translate-y-0" : "-translate-y-full"
        }`}
      >
        <div className="container mx-auto px-4 py-3 border-0">
          <div className="flex items-center justify-between">
            <Link
              href="/"
              className="flex items-center gap-2 hover:opacity-80 transition-opacity"
            >
              <img
                src="/images/logo/howthef_logo_800_x_800.svg"
                alt="howtheF logo"
                className="w-10 h-10"
              />
              <span className="text-xl font-display font-bold text-gray-900">
                howtheF
              </span>
            </Link>

            {/* 🔥 DESKTOP NAVIGATION */}
            <nav className="hidden md:flex items-center gap-6 lg:gap-8">
              {currentNavigation.map((item) => (
                <Link
                  key={item.name}
                  href={item.href}
                  className={`text-sm font-display font-semibold transition-all duration-200 ${
                    pathname === item.href
                      ? "text-teal-dark"
                      : "text-gray-700 hover:text-teal-dark"
                  }`}
                >
                  {item.name}
                </Link>
              ))}
            </nav>

            <div className="flex items-center gap-3">
              {renderUserActions()}

              {/* 🔥 MOBILE HAMBURGER MENU BUTTON */}
              <button
                onClick={toggleMobileMenu}
                className="md:hidden flex items-center justify-center w-10 h-10 rounded-lg hover:bg-gray-100 transition-colors"
                aria-label={
                  isMobileMenuOpen ? "Close mobile menu" : "Open mobile menu"
                }
                aria-expanded={isMobileMenuOpen}
              >
                <span className="sr-only">
                  {isMobileMenuOpen ? "Close mobile menu" : "Open mobile menu"}
                </span>
                {isMobileMenuOpen ? (
                  <X size={20} className="text-gray-700" aria-hidden="true" />
                ) : (
                  <Menu
                    size={20}
                    className="text-gray-700"
                    aria-hidden="true"
                  />
                )}
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* 🔥 MOBILE MENU OVERLAY */}
      {isMobileMenuOpen && (
        <div className="fixed inset-0 z-40 md:hidden">
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-black/20 backdrop-blur-sm"
            onClick={() => setIsMobileMenuOpen(false)}
          />

          {/* Mobile Menu */}
          <div
            ref={mobileMenuRef}
            className="fixed top-[73px] left-0 right-0 bg-white max-h-[calc(100vh-73px)] overflow-y-auto"
          >
            <div className="px-4 py-6 space-y-6">
              {/* Navigation Links */}
              <nav className="space-y-4">
                {currentNavigation.map((item) => (
                  <Link
                    key={item.name}
                    href={item.href}
                    className={`block px-4 py-3 rounded-lg text-base font-display font-semibold transition-all duration-200 ${
                      pathname === item.href
                        ? "text-teal-dark bg-teal/5"
                        : "text-gray-700 hover:text-teal-dark hover:bg-teal/5"
                    }`}
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    {item.name}
                  </Link>
                ))}
              </nav>

              {/* Mobile User Actions */}
              {!isAuthenticated && (
                <div className="space-y-3 pt-4 border-t border-gray-200">
                  <Link
                    href="/login"
                    className="block w-full text-center px-4 py-3 text-base font-display font-semibold text-gray-700 hover:text-teal-dark transition-colors rounded-lg hover:bg-teal/5"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    Login
                  </Link>
                  <Link
                    href="/contact?source=mobile_menu"
                    className="block w-full"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    <HTFButton
                      variant="primary"
                      className="w-full justify-center text-black"
                    >
                      Book Discovery Call
                    </HTFButton>
                  </Link>
                </div>
              )}

              {/* Mobile Authenticated User Menu */}
              {isAuthenticated && (
                <div className="space-y-3 pt-4 border-t border-gray-200">
                  <div className="px-4 py-3 bg-gray-50 rounded-lg">
                    <p className="text-xs text-gray-500 uppercase tracking-wider">
                      Signed in as
                    </p>
                    <p className="text-sm font-semibold text-gray-900 truncate">
                      {user?.email}
                    </p>
                    <div className="flex items-center gap-1 mt-1">
                      <div
                        className={`w-2 h-2 rounded-full ${hasActiveSubscription ? "bg-green-500" : "bg-gray-400"}`}
                      />
                      <p className="text-xs text-gray-500">
                        {hasActiveSubscription
                          ? PLAN_NAMES[subscription?.priceId || ""] || "Pro"
                          : "Free Plan"}
                      </p>
                    </div>
                  </div>

                  <button
                    onClick={() => {
                      router.push("/dashboard");
                      setIsMobileMenuOpen(false);
                    }}
                    className="w-full flex items-center gap-3 px-4 py-3 text-base font-display font-medium text-gray-700 hover:bg-gray-50 text-left transition-colors rounded-lg"
                  >
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z"
                      />
                    </svg>
                    Dashboard
                  </button>

                  <button
                    onClick={() => {
                      router.push("/ai-workflows");
                      setIsMobileMenuOpen(false);
                    }}
                    className="w-full flex items-center gap-3 px-4 py-3 text-base font-display font-medium text-gray-700 hover:bg-gray-50 text-left transition-colors rounded-lg"
                  >
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
                      />
                    </svg>
                    My AI Workflows
                  </button>

                  {!hasActiveSubscription && (
                    <Link
                      href="/pricing"
                      className="w-full flex items-center gap-3 px-4 py-3 text-base font-display font-medium text-gray-700 hover:bg-gray-50 text-left transition-colors rounded-lg"
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      <svg
                        className="w-5 h-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z"
                        />
                      </svg>
                      Upgrade Plan
                    </Link>
                  )}

                  <button
                    onClick={() => {
                      router.push("/organizations");
                      setIsMobileMenuOpen(false);
                    }}
                    className="w-full flex items-center gap-3 px-4 py-3 text-base font-display font-medium text-gray-700 hover:bg-gray-50 text-left transition-colors rounded-lg"
                  >
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 8h5"
                      />
                    </svg>
                    My Workspaces
                  </button>

                  <button
                    onClick={() => {
                      router.push("/settings/referrals");
                      setIsMobileMenuOpen(false);
                    }}
                    className="w-full flex items-center gap-3 px-4 py-3 text-base font-display font-medium text-teal-dark hover:bg-teal/5 text-left transition-colors rounded-lg"
                  >
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"
                      />
                    </svg>
                    Invite a Friend
                  </button>

                  <button
                    onClick={() => {
                      router.push("/settings");
                      setIsMobileMenuOpen(false);
                    }}
                    className="w-full flex items-center gap-3 px-4 py-3 text-base font-display font-medium text-gray-700 hover:bg-gray-50 text-left transition-colors rounded-lg"
                  >
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
                      />
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                      />
                    </svg>
                    Account Settings
                  </button>

                  {showAdminLink && (
                    <button
                      onClick={() => {
                        router.push("/admin");
                        setIsMobileMenuOpen(false);
                      }}
                      className="w-full flex items-center gap-3 px-4 py-3 text-base font-display font-medium text-amber-700 hover:bg-amber-50 text-left transition-colors rounded-lg"
                    >
                      <svg
                        className="w-5 h-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4"
                        />
                      </svg>
                      Admin Dashboard
                    </button>
                  )}

                  <div className="border-t border-gray-200 my-3" />
                  <button
                    onClick={handleSignOut}
                    className="w-full flex items-center gap-3 px-4 py-3 text-base font-display font-medium text-red-600 hover:bg-red-50 text-left transition-colors rounded-lg"
                  >
                    <LogOut size={20} />
                    Logout
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* 🔥 SPACER to prevent content jumping when header becomes fixed */}
      <div className="h-[73px] bg-beige-dark" />
    </>
  );
}
